/**
 * HTML Renderer
 *
 * Pure renderer component for HTML content (Caselaw and LRR references).
 * Fetches HTML from API endpoints and displays it.
 *
 * This component only handles rendering - no header, no layout.
 * Parent wrapper provides the UI chrome.
 */

import { sanitizeAndHighlightArtifactHtml, sanitizeAndHighlightHtml } from '@/content-viewer/util/sanitize-and-highlight-html'
import { replaceReferenceMarkersWithLinks, type ReferenceInfoByCitationId } from '@/artifact-editor/utils/citation-helpers'
import { useEffect, useRef } from 'react'
import { Parser } from 'html-to-react'

type HTMLBaseRendererProps = {
  htmlContent: string
  highlightSentences?: string[] // Array of sentences to highlight (from highlighted_citations)
  referenceInfoByCitationId?: ReferenceInfoByCitationId
}

/**
 * HTMLRenderer Component
 *
 * Renders HTML content fetched from API endpoints for Caselaw and LRR references.
 */
export default function HTMLBaseRenderer(props: HTMLBaseRendererProps): JSX.Element {
  const { htmlContent, highlightSentences, referenceInfoByCitationId } = props

  const containerRef = useRef<HTMLDivElement>(null)

  const htmlParser = Parser()

  // Sanitize and apply highlights. The artifact variant keeps citation-id anchors,
  // which the post-pass below rewrites to reference-viewer links or unwraps to plain
  // authority text; source documents (caselaw/LRR) use the strict variant.
  const sanitizedHtml = referenceInfoByCitationId
    ? sanitizeAndHighlightArtifactHtml(htmlContent || '', highlightSentences)
    : sanitizeAndHighlightHtml(htmlContent || '', highlightSentences)
  const processedHtml = referenceInfoByCitationId ? replaceReferenceMarkersWithLinks(sanitizedHtml, referenceInfoByCitationId) : sanitizedHtml
  const hasHighlights = Boolean(highlightSentences && highlightSentences.length > 0)

  // Parse the HTML (already sanitized and highlighted)
  const parsedHtml = htmlParser.parse(processedHtml)

  // Scroll to first highlight if present
  useEffect(() => {
    // Only scroll if highlights exist
    if (!hasHighlights) return

    // Use setTimeout to ensure DOM is fully rendered
    const timer = setTimeout(() => {
      if (containerRef.current) {
        const firstHighlight = containerRef.current.querySelector('.ref-highlight')
        if (firstHighlight) {
          firstHighlight.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          })
        }
      }
    }, 250)

    return () => clearTimeout(timer)
  }, [processedHtml, hasHighlights])

  // Loading State
  return (
    <div className="relative h-full flex flex-col items-center p-3 animate-fadeIn">
      <div ref={containerRef} className="view-container gcs-md-highlights flex-grow border bg-white w-full">
        {parsedHtml}
      </div>
      <div>&nbsp;</div>
    </div>
  )
}
